import React from 'react';
import { useForm, Controller, ErrorMessage } from 'react-hook-form';
import styled from 'styled-components';
import {
  colors,
  Button,
  Link,
  LoadingSpinner,
  PhoneNumberInput,
  SelectInput,
  Text,
  TextInput,
  utils,
} from 'shared-library';
import { Row, Column } from '../Layout';
import { Patient } from '../../store/patients/types';
import { countrySelectOptions } from '../../lib';

const ErrorText = styled(Text).attrs({
  color: colors.error,
  weight: '600',
})`
  font-size: 12px;
`;

const Actions = styled(Column)`
  margin: 32px auto 16px;
  text-align: center;
`;

const CancelButton = styled(Link).attrs({
  as: 'button',
})`
  background-color: transparent;
  border: 0;
  margin: 0 20px 10px 0;
`;

export type ContactInformationInput = {
  email: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: {
    label: string;
    value: string;
  };
  phoneNumber: string;
};

interface ContactInformationFormProps {
  isSaving: boolean;
  onCancelEdit: () => void;
  onSubmit: (data: ContactInformationInput) => void;
  contact: Patient;
}

const ContactInformationForm = ({
  isSaving,
  onCancelEdit,
  onSubmit,
  contact,
}: ContactInformationFormProps) => {
  const { control, errors, handleSubmit, watch } = useForm({
    defaultValues: {
      ...contact,
      country: countrySelectOptions.find((_) => _.value === contact.country),
    },
  });
  
  const selectedCountry = watch('country');
  const isUK = selectedCountry?.value === 'United Kingdom';
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Column>
          <Controller
            as={TextInput}
            control={control}
            label="Personal Email Address"
            name="email"
            rules={utils.validate('email', 'required')}
          />
          <ErrorMessage as={ErrorText} errors={errors} name="email" />
        </Column>
        <Column>
          <Controller
            as={PhoneNumberInput}
            control={control}
            label="Phone Number"
            name="phoneNumber"
            rules={utils.validate('phoneNumber', 'required')}
          />
          <ErrorMessage as={ErrorText} errors={errors} name="phoneNumber" />
        </Column>
      </Row>
      <Row>
        <Column>
          <Controller
            as={TextInput}
            control={control}
            label="Street Address w/ Apt #"
            name="address"
            rules={utils.validate('address', 'required')}
          />
          <ErrorMessage as={ErrorText} errors={errors} name="address" />
        </Column>
        <Column>
          <Controller
            as={TextInput}
            control={control}
            label="City"
            name="city"
            rules={utils.validate('city', 'required')}
          />
          <ErrorMessage as={ErrorText} errors={errors} name="city" />
        </Column>
        <Column>
          <Controller
            as={TextInput}
            control={control}
            label="Zip / Postal Code"
            name="postalCode"
            rules={utils.validate('postalCode', 'required')}
          />
          <ErrorMessage as={ErrorText} errors={errors} name="postalCode" />
        </Column>
      </Row>
      <Row>
        <Column>
          <Controller
            as={SelectInput}
            control={control}
            defaultValue={countrySelectOptions.find((c) => c.value === contact.country)}
            label="Country"
            name="country"
            options={countrySelectOptions}
            rules={utils.validate('country', 'required')}
          />
          <ErrorMessage
            as={ErrorText}
            errors={errors}
            name="country"
            message={errors.country?.label?.message || errors.country?.value?.message}
          />
        </Column>
        <Column>
          <Controller
            as={TextInput}
            control={control}
            label="State / Province"
            name="state"
            rules={isUK ? {} : utils.validate('state', 'required')}
          />
          <ErrorMessage as={ErrorText} errors={errors} name="state" />
        </Column>
      </Row>
      <Row>
        <Actions>
          <CancelButton onClick={onCancelEdit} variant="primary" size="medium">
            Cancel
          </CancelButton>
          <Button disabled={isSaving} type="submit" variant="primary" size="medium">
            {isSaving ? <LoadingSpinner color={colors.white} size="small" /> : 'Save'}
          </Button>
        </Actions>
      </Row>
    </form>
  );
};

export default ContactInformationForm;
